import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {AssociationConfiguration} from '../../models/association.config';
import {Events, TextInput, ViewController} from 'ionic-angular';
import {TranslateService} from '@ngx-translate/core';
import {PadRfidPipe} from '../../pipes/validate-tag/pad-rfid';
import {isUndefined} from 'ionic-angular/util/util';
import {PadUpcPipe} from '../../pipes/validate-tag/pad-upc';
import {VizixService} from '../../providers/vizix.service';
import {ReaderService} from '../../providers/reader.service';
import {READER_TYPE_BARCODE} from '../../app/app.constants';
import {TagReadBarcodeResponse} from '../../models/reader-model/tagReadBarcodeResponse';
import {Keyboard} from '@ionic-native/keyboard';
import {EncodeTagConfiguration} from '../../models/encodeTag.config';

@Component({
  selector: 'upc-epc-grid',
  templateUrl: 'upc-epc-grid.html'
})

export class UpcEpcGridComponent implements OnInit, OnDestroy {

  @Input('config') config: AssociationConfiguration | EncodeTagConfiguration | any;
  @Input('firstField') firstField: string;
  @Input('secondField') secondField: string;
  @Input('tagType') TAG_TYPE: string;
  @Input('stopReader') STOP_READER: boolean;

  @Output('onSubmit') submit: EventEmitter<any> = new EventEmitter();
  @Output('onAlert') alert: EventEmitter<any> = new EventEmitter();

  @ViewChild('inputUPC') inputFirstField: TextInput;
  @ViewChild('inputRFID') inputSecondField: TextInput;

  public isCameraEnabled = false;
  public isSoundErrorReady: boolean;
  public soundError: string = 'trackError';
  public firstFieldBarcodeEnabled = false;
  public secondFieldBarcodeEnabled = false;

  private tag = '[ASSOCIATE_TAGS]';
  private validationFirstFieldWithoutQuantifiers: string;
  private validationSecondFieldWithoutQuantifiers: string;
  private barcodeAttempts = 0;
  /**
   * Make the current view's content scroll to (x,y) coordinates in a custom timeout delay
   * @param {number} x
   * @param {number} y
   * @param {number} timeout
   */
  private scrollContent = (x: number = 0, y: number = 0, timeout: number = 100) => {
    const content = this.viewCtrl.getContent();
    if (content) {
      setTimeout(() => this.viewCtrl.getContent().scrollTo(x, y), timeout);
    }
  };

  /**
   * UPC EPC generic field form component
   * Handles Barcode and RFID readings for UPC and EPC validation
   *
   * @param {ViewController} viewCtrl
   * @param {TranslateService} translate
   * @param {Events} events
   * @param {VizixService} vizixService
   * @param {ReaderService} readerService
   * @param {ChangeDetectorRef} cdr
   * @param {NgZone} ngZone
   * @param {Keyboard} keyBoard
   */
  constructor(public viewCtrl: ViewController,
              private translate: TranslateService,
              private events: Events,
              private vizixService: VizixService,
              private readerService: ReaderService,
              private cdr: ChangeDetectorRef,
              private ngZone: NgZone,
              private keyBoard: Keyboard) {
  }

  /**
   * Triggered when component is rendered
   */
  public ngOnInit() {
    this.subscribeEvents();
    this.initializeVariables();
    this.initializeChangeDetection();
  }

  /**
   * Triggered when component is removed from DOM
   */
  public ngOnDestroy() {
    this.unSubscribeEvents();
    this.terminateChangeDetection();
  }

  /**
   * Emit associate event with field values to parent component
   */
  public onSubmit() {

    const timeTimeOut: number = this.config.AutoSaveTimer.value || 0;
    const triggerAssociation = () => {
      this.submit.emit({
        config: this.config,
        firstField: this.firstField,
        secondField: this.secondField
      })
    };

    if (this.validateFields()) {
      this.stopScanReader();
      setTimeout(triggerAssociation, timeTimeOut);
    }
  }

  /**
   * Triggered on first field keydown event
   * @param event
   */
  public onKeyDownFirstField(event) {
    let charPressed = event.charAt(event.length - 1);
    if (!charPressed) {
      charPressed = '';
    }
    let regExpression = new RegExp(this.validationFirstFieldWithoutQuantifiers);
    if (!regExpression.test(<string>charPressed.toUpperCase())) {
      this.firstField = event.replace(charPressed, '');
      this.inputFirstField.value = this.firstField;
      this.cdr.detectChanges();
    }
    else {
      this.firstField = event;
    }
  }

  /**
   * Triggered on second field keydown event
   * @param event
   */
  public onKeyDownSecondField(event) {
    let charPressed = event.charAt(event.length - 1);
    if (!charPressed) {
      charPressed = '';
    }

    let regExpression = new RegExp(this.validationSecondFieldWithoutQuantifiers);
    if (!regExpression.test(charPressed.toUpperCase())) {
      this.secondField = event.replace(charPressed, '');
      this.inputSecondField.value = this.secondField;
      this.cdr.detectChanges();
    }
    else {
      this.secondField = event;
    }
  }

  /**
   * Triggered when ENTER key is pressed on first field
   */
  public onKeyEnterFirstField() {
    this.inputSecondField.setFocus();
    if (this.viewCtrl.getContent() && this.config.AllowKeyboardInputForAssociations) {
      this.scrollContent(0, 70);
    }
  }

  /**
   * Triggered when ENTER key is pressed on second field
   */
  public onKeyEnterSecondField() {
    if (this.config.AutoSave.value) {
      this.onSubmit();
    }
  }

  /**
   * Clear first field
   */
  public clearFirstField() {
    this.firstField = '';
    this.inputFirstField.setFocus();
  }

  /**
   * Clear second field
   */
  public clearSecondField() {
    this.scrollContent(0, 80);
    this.secondField = '';
    this.inputSecondField.setFocus();
  }

  /**
   * Open barcode camera plugin for first field
   */
  public openBarcodeCamera(model, submit: boolean = false, element?: TextInput) {
    const handleBarcodeReading = (barcode: { value: string }) => {
      model = barcode.value;
      if (submit) {
        this.onSubmit();
      }
      if (element instanceof TextInput) {
        element.setFocus();
      }
    };

    const handleBarcodeError = (error) => {
      console.log(this.tag, 'Error barcode camera', error);
    };

    this.readerService.initBarcodeCameraScan().then(handleBarcodeReading, handleBarcodeError);
  }

  /**
   * Triggered on focus event for the first field
   */
  public onFocusUPC() {
    this.TAG_TYPE = this.config.FirstFieldLabel.value;
    this.changeReaderMode(this.config.FirstFieldReaderType.value);
  }

  /**
   * Triggered on focus event for the second field
   */
  public onFocusRFID() {
    this.TAG_TYPE = this.config.SecondFieldLabel.value;
    this.changeReaderMode(this.config.SecondFieldReaderType.value);
  }

  /**
   * Initialize variable values
   */
  private initializeVariables() {
    this.firstField = '';
    this.secondField = '';
    this.firstFieldBarcodeEnabled = this.config.FirstFieldReaderType.value == READER_TYPE_BARCODE;
    this.secondFieldBarcodeEnabled = this.config.SecondFieldReaderType.value == READER_TYPE_BARCODE;
    this.validationFirstFieldWithoutQuantifiers = this.vizixService.removeQuantifiersOfRegularExpression(this.config.FirstFieldValidation.value);
    this.validationSecondFieldWithoutQuantifiers = this.vizixService.removeQuantifiersOfRegularExpression(this.config.SecondFieldValidation.value);
  }

  /**
   * Subscribe to component events
   */
  private subscribeEvents() {
    this.viewCtrl.didLeave.subscribe(() => this.stopScanReader());
    this.events.subscribe('upcEpcGrid:stopScanReader', () => this.stopScanReader());
    this.events.subscribe('upcEpcGrid:firstFieldFocus', () => this.inputFirstField.setFocus());
  }

  /**
   * Unsubscribe to component events
   */
  private unSubscribeEvents() {
    this.viewCtrl.didLeave.unsubscribe();
    this.events.unsubscribe('upcEpcGrid:stopScanReader', () => this.stopScanReader());
    this.events.unsubscribe('upcEpcGrid:firstFieldFocus', () => this.inputFirstField.setFocus());
  }

  /**
   * Initialize change detection
   */
  private initializeChangeDetection() {
    this.cdr.reattach();
    this.cdr.detectChanges();
  }

  /**
   * Terminate change detection
   */
  private terminateChangeDetection() {
    this.cdr.detach();
  }

  /**
   * Switch between reader modes
   * @param {string} readerType
   */
  private changeReaderMode(readerType: string) {
    if (readerType == READER_TYPE_BARCODE) {
      this.changeBarcodeMode();
    } else {
      this.changeRFIDMode();
    }
  }

  /**
   * Initialize the barcode reader mode
   */
  private initBarcodeMode() {
    if (!this.readerService.continueWithoutReader) {
      console.log(this.tag, 'Changing to Barcode mode');
      this.readerService.initBarcodeScan().subscribe(
        (result: TagReadBarcodeResponse) => {
          this.barcodeAttempts = 0;
          console.log(this.tag, 'result', result);
          if (result.value != null) {
            this.showScannedTag(result.value);
          }
        },
        (e) => {
          console.log(this.tag, 'error initBarcodeScan');
          console.log(this.tag, e);
          if (e.code == 800 && this.barcodeAttempts <= 3) {
            this.barcodeAttempts++;
            setTimeout(() => {
              this.initBarcodeMode();
            }, 500);
          }
        }
      );
    }
  }

  /**
   * Changes configuration to barcode when radio button value=barcode
   **/
  private changeBarcodeMode() {
    this.readerService.isDeviceBarcode()
      .subscribe(
        (result) => {
          console.log(this.tag, result);
          if (result) {
            console.log(this.tag, 'Device is barcode enabled');
            this.readerService.initScanDeviceBarcode().subscribe(
              (result: TagReadBarcodeResponse) => {
                console.log(this.tag, 'Device Read', result);
                if (result.value != null) {
                  this.showScannedTag(result.value);
                }
              },
              (error) => {
                console.log(this.tag, 'Error in device barcode initialization');
                console.log(this.tag, error);
              }
            );
            this.initBarcodeMode();
          } else {
            console.log(this.tag, 'Device is not barcode enabled');
            console.log(this.tag, 'continue without reader', this.readerService.continueWithoutReader);
            this.initBarcodeMode();
          }
        },
        (error) => {
          console.log(this.tag, error);
        }
      );
  }

  /**
   * Changes configuration to RFID when radio button value=rfid
   **/
  private changeRFIDMode() {

    const handleInitSingleScan = (result) => {
      if (result.value != null) {
        this.showScannedTag(result.value);
      }
    };

    if (!this.readerService.continueWithoutReader) {
      this.readerService.initSingleScan().subscribe(handleInitSingleScan);
    }
  }

  /**
   * Update first or second field accordingly after validation
   * Emits alerts to parent component if validation fails
   * @param {string} tag
   */
  private showScannedTag(tag: string) {
    this.ngZone.run(() => {
      if (this.TAG_TYPE == this.config.FirstFieldLabel.value) {
        tag = this.normalizeField(tag);
        this.onAlert("dismiss");
        if (this.validateFirstField(tag)) {
          this.firstField = tag;
          this.inputSecondField.setFocus();
          if (this.config.AllowKeyboardInputForAssociations) {
            this.scrollContent(0, 80);
          }
        }
        else {
          this.onAlert(
            'error',
            this.translate.instant('manage_tags.dialog_error_invalid_upc_title').replace('${firstFieldLabel}', this.config.FirstFieldLabel.value),
            this.translate.instant('manage_tags.dialog_error_invalid_upc_message').replace('${firstFieldLabel}', this.config.FirstFieldLabel.value),
            this.translate.instant('manage_tags.buttons.ok')
          );
        }
      }
      else if (this.TAG_TYPE == this.config.SecondFieldLabel.value) {
        tag = new PadRfidPipe(this.vizixService).transform(tag);
        this.onAlert("dismiss");
        if (this.validateSecondField(tag)) {
          this.secondField = tag;
          this.keyBoard.close();
          if (this.config.AutoSave.value) {
            this.onSubmit();
          }
        }
        else {
          this.onAlert(
            'error',
            this.translate.instant('manage_tags.dialog_error_invalid_epc_title').replace('${secondFieldLabel}', this.config.SecondFieldLabel.value),
            this.translate.instant('manage_tags.dialog_error_invalid_epc_message').replace('${secondFieldLabel}', this.config.SecondFieldLabel.value),
            this.translate.instant('manage_tags.buttons.ok')
          );
        }
      }
    });
  }

  /**
   * Normalize first field
   */
  private normalizeField(input: string): string {
    const type = this.config.constructor.name;
    let field: string = input;
    switch (type) {
      case 'AssociationConfiguration':
        field = new PadUpcPipe(this.vizixService).transform(input);
        break;
      case 'EncodeTagConfiguration':
        field = new PadUpcPipe(this.vizixService).transform(input, this.config.FirstFieldAutoPad.value);
        break;
    }
    return field;
  }

  /**
   * Returns true if first and second fields are valid UPC/EPC values
   * @returns {boolean}
   */
  private validateFields(): boolean {

    this.firstField = this.firstField.trim().toUpperCase();
    this.secondField = this.secondField.trim().toUpperCase();

    let message: string;
    let title: string = this.translate.instant('manage_tags.dialog_required_title');
    let button: string = this.translate.instant('manage_tags.buttons.ok');

    if (!this.firstField.length || !this.secondField.length) {
      if (!this.firstField.length) {
        message = this.translate.instant('manage_tags.dialog_required_firstField');
        message = message.replace('${firstFieldLabel}', this.config.FirstFieldLabel.value);
      }
      if (!this.secondField.length) {
        message = this.translate.instant('manage_tags.dialog_required_secondField');
        message = message.replace('${secondFieldLabel}', this.config.SecondFieldLabel.value);
      }
      this.onAlert('alert', title, message, button);
      return false;
    } else {

      // After we ensure that first and second Fields have values we pad those
      this.secondField = new PadRfidPipe(this.vizixService).transform(this.secondField);
      this.firstField = this.normalizeField(this.firstField);

      if (!this.validateFirstField() || !this.validateSecondField()) {
        if (!this.validateFirstField()) {
          message = this.translate.instant('manage_tags.dialog_error_invalid_upc_message');
          message = message.replace('${firstFieldLabel}', this.config.FirstFieldLabel.value);
        }

        if (!this.validateSecondField()) {
          message = this.translate.instant('manage_tags.dialog_error_invalid_epc_message');
          message = message.replace('${secondFieldLabel}', this.config.SecondFieldLabel.value);
        }
        this.onAlert('error', title, message, button);
        return false;
      }

    }
    return true;

  }

  /**
   * Terminate the reader's scanning process
   */
  private stopScanReader() {
    if (this.STOP_READER) {
      this.readerService.stopScan().subscribe(
        (result) => {
          console.log(this.tag, 'StopScan');
          console.log(this.tag, result);
        },
        (e) => {
          console.log(this.tag, 'ErrorStop');
          console.log(this.tag, e);
        }
      );
    }
  }

  /**
   * Validate first field
   * @param {string} upc
   * @returns {boolean}
   */
  private validateFirstField(upc: string = this.firstField): boolean {
    let regExpression = new RegExp(this.config.FirstFieldValidation.value);
    return !(isUndefined(upc) || upc == null || upc == '' || !regExpression.test(upc));
  }

  /**
   * Validate second field
   * @param {string} epc
   * @returns {boolean}
   */
  private validateSecondField(epc: string = this.secondField): boolean {
    let regExpression = new RegExp(this.config.SecondFieldValidation.value);
    return !(isUndefined(epc) || epc == null || epc == '' || !regExpression.test(epc));
  }

  /**
   * Emit alert action to parent component
   * @param {"alert" | "cancel" | "error" | "dismiss"} type
   * @param {string} title
   * @param {string} message
   * @param {string} okButtonText
   */
  private onAlert(type: 'alert' | 'cancel' | 'error' | 'dismiss', title?: string, message?: string, okButtonText?: string) {
    this.alert.emit({type: type, title: title, message: message, okButtonText: okButtonText});
  }


}



// WEBPACK FOOTER //
// ./src/components/upc-epc-grid/upc-epc-grid.ts